import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import {
  Menu,
  X,
  Sun,
  Moon,
  Home,
  Users,
  Briefcase,
  Mail,
  Globe,
} from "lucide-react";

const Header = ({ isDarkMode, setIsDarkMode, scrollToSection }) => {
  const { language, translations, changeLanguage } = useLanguage();
  const header = translations[language].header;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("inicio");

  const getIconForMenuItem = (id) => {
    const iconMap = {
      inicio: <Home size={16} />,
      nosotros: <Users size={16} />,
      servicios: <Briefcase size={16} />,
      contacto: <Mail size={16} />,
    };
    return iconMap[id] || <Home size={16} />;
  };

  const menuItems = header.menuItems.map((item) => ({
    id: item.id,
    label: item.label,
    anchor: item.anchor,
    icon: getIconForMenuItem(item.id),
  }));

  const languages = header.languages;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = menuItems.map((item) => item.id);
      const current = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    setIsMenuOpen(false);
    setActiveSection(sectionId);
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? isDarkMode
            ? "bg-gray-900/98 shadow-lg"
            : "bg-white/98 shadow-lg"
          : isDarkMode
          ? "bg-gray-900/95"
          : "bg-white/95"
      } backdrop-blur border-b ${
        isDarkMode ? "border-gray-800" : "border-gray-200"
      }`}
    >
      <nav
        className={`container mx-auto px-4 transition-all duration-300 ${
          scrolled ? "py-3" : "py-4"
        }`}
      >
        <div className="flex justify-between items-center">
          <div
            className="flex items-center cursor-pointer group"
            onClick={() => handleNavClick("inicio")}
          >
            <img
              src="/images/logom.svg"
              alt="Heuristiko Logo"
              className={`h-10 w-auto md:h-12 object-contain transition-all duration-300 group-hover:scale-110 ${
                scrolled ? "md:h-10" : "md:h-12"
              }`}
            />
            <div className=" md:block text-gray-900 dark:text-white">
              <h1 className="text-xl font-bold tracking-wide">HEURISTIKO</h1>
              <p className="text-xs tracking-widest text-gray-500 dark:text-gray-400 text-gray-500">
                MÉTODO
              </p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`relative font-medium transition-all duration-300 hover:text-[#12A9B0] ${
                  activeSection === item.id
                    ? "text-[#12A9B0]"
                    : isDarkMode
                    ? "text-gray-300"
                    : "text-gray-700"
                }`}
              >
                <span className="flex items-center gap-2">{item.label}</span>
                {activeSection === item.id && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#12A9B0] rounded-full animate-slideIn"></span>
                )}
              </button>
            ))}
            <div className="relative group z-[3333]">
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 text-gray-300"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                <Globe size={20} />
                <span className="text-sm font-medium">
                  {languages.find((l) => l.code === language)?.name}
                </span>
              </button>

              <div className="absolute right-0 mt-2 py-2 w-32 bg-white text-gray-600 dark:bg-gray-800 dark:text-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                      language === lang.code
                        ? "bg-gray-100 dark:bg-gray-700"
                        : "bg-gray-100 dark:bg-gray-700"
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2.5 rounded-lg transition-all duration-300 hover:scale-110 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-yellow-400 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <div className="relative group">
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                  isDarkMode ? " text-white" : " text-gray-700"
                }`}
              >
                <Globe size={20} />
                <span className="text-sm font-medium">
                  {languages.find((l) => l.code === language)?.name}
                </span>
              </button>

              <div className="absolute right-0 mt-2 py-2 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full px-4 py-2 text-left hover:bg-gray-400 dark:hover:bg-gray-700 flex items-center gap-2 ${
                      language === lang.code
                        ? "text-white bg-gray-700 "
                        : "text-gray-500 "
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="p-2 rounded-lg transition-all duration-300 text-gray-700 dark:text-yellow-400"
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 transition-all duration-300 text-gray-700 dark:text-white"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X size={24} className="animate-spin-once" />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        </div>

        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
          }`}
        >
          <div className="py-4 border-t border-gray-200 dark:border-gray-800">
            {menuItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg transition-all duration-300 ${
                  activeSection === item.id
                    ? "bg-[#12A9B0]/10 text-[#12A9B0]"
                    : isDarkMode
                    ? "text-gray-300 hover:bg-gray-800"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                style={{
                  animationDelay: `${index * 0.05}s`,
                  animation: isMenuOpen
                    ? "slideDown 0.3s ease-out forwards"
                    : "",
                }}
              >
                <span className="text-[#12A9B0]">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gray-200 dark:bg-gray-800">
        <div
          className="h-full bg-gradient-to-r from-[#12A9B0] to-[#0e8d92] transition-all duration-300"
          style={{
            width: `${
              (menuItems.findIndex((item) => item.id === activeSection) + 1) *
              25
            }%`,
          }}
        />
      </div>
    </header>
  );
};

export default Header;
